import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import {
  getUsersCount,
  getEscalationPoliciesCount,
  getSchedulesCount,
} from '../../redux/fe-data/selectors';
import FreemiumNotificationBar from './FreemiumNotificationBar';
import FreemiumModal from './FreemiumModal';
import {
  FREEMIUM_LIMIT_NOTIFICATIONS,
  FREEMIUM_LIMITS,
  FREEMIUM_DEFAULT_NOTIFICATION,
} from './nurture-content';
import { blue as FreemiumBlue } from './FreemiumNotificationBar/styles.module.css';
import {
  trackEvent,
  NURTURE_BAR_TYPES,
  CLICKED_NURTURE_BANNER,
  NURTURE_BAR_POPUP,
} from '../../util/segment';


// Since the string method 'startsWith()' is not IE11 compatible,
// this modified polyfill from Mozilla docs is necessary.
const startsWith = (inputString, search, rawPos = 0) => {
  const pos = rawPos > 0 ? Math.floor(rawPos) : 0;
  return inputString.substring(pos, pos + search.length) === search;
};

class FreemiumNurtureBar extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showModalFlag: false,
      currentFreemiumNotification: this.getNotificationInformation(),
    };
  }

  componentDidMount() {
    window.addEventListener('emberRouteTransition', () => (
      this.setState({ currentFreemiumNotification: this.getNotificationInformation() })
    ));
  }

  stringifyNotificationDescription = (description) => {
    const children = React.Children.toArray(description.props.children);
    return children.map(child => (
      React.isValidElement(child) ? this.stringifyNotificationDescription(child) : child
    )).join('');
  }

  getBannerClickSegmentInformation = () => {
    const { notification, limit } = this.state.currentFreemiumNotification;
    return {
      ctaType: limit.ctaType || 'Generic',
      message: this.stringifyNotificationDescription(notification.description(limit.type)),
      notificationType: notification.type.segmentNotificationType,
      nurtureBarType: NURTURE_BAR_TYPES.freemium,
    };
  }

  getPopupSegmentInformation = () => {
    const { notification } = this.state.currentFreemiumNotification;
    return {
      ctaType: null,
      nurtureBarType: NURTURE_BAR_TYPES.freemium,
      message: notification.modal.title,
    };
  }

  toggleShowModalFlagOpen = () => {
    trackEvent(CLICKED_NURTURE_BANNER, {
      ...this.getBannerClickSegmentInformation()
    });

    trackEvent(NURTURE_BAR_POPUP, {
      ...this.getPopupSegmentInformation()
    });

    this.setState({
      showModalFlag: true,
    });
  }

  toggleShowModalFlagClose = () => {
    this.setState({
      showModalFlag: false
    });
  }

  isUserLimitInCautionState = () => (this.props.currentAccountUsage.users
    === FREEMIUM_LIMITS.users.limitMax - 1);

  // This method returns the freemiuim limit key that is related to the current URL.
  // For example if the current path starts with /users, it returns the key 'users'
  getFreemiumLimitKey = currentPath => (Object.keys(FREEMIUM_LIMITS).find(
    key => startsWith(currentPath, FREEMIUM_LIMITS[key].url)
  ));

  // This method returns a notification object. On users, schedules and EP routes,
  // a maxedout, caution, or default notification is returned, based on that limit's
  // current usage. On all other routes, the base notification is returned.
  getNotificationInformation = () => {
    const currentPath = window.location.pathname;
    const freemiumLimitKey = this.getFreemiumLimitKey(currentPath);
    const currentLimitUsage = this.props.currentAccountUsage[freemiumLimitKey] || 0;
    const freemiumLimit = FREEMIUM_LIMITS[freemiumLimitKey] || {};
    let notificationType = FREEMIUM_LIMIT_NOTIFICATIONS.default[freemiumLimitKey || 'base'];

    // Show default messaging if no limit notification
    if (!freemiumLimitKey) {
      return {
        ...FREEMIUM_DEFAULT_NOTIFICATION
      };
    }

    if (currentLimitUsage >= freemiumLimit.limitMax) {
      notificationType = FREEMIUM_LIMIT_NOTIFICATIONS.maxedOut;
    // Currently, only the users limit has a cautions state
    // This will change as more limits are implemented, such as SMS notifications.
    } else if (freemiumLimitKey === 'users' && this.isUserLimitInCautionState()) {
      notificationType = FREEMIUM_LIMIT_NOTIFICATIONS.caution;
    }

    return {
      notification: notificationType,
      limit: freemiumLimit,
    };
  }

  render() {
    const { notification, limit } = this.state.currentFreemiumNotification;

    return (
      <React.Fragment>
        <FreemiumNotificationBar
          description={notification.description}
          openModal={this.toggleShowModalFlagOpen}
          notificationType={notification.type}
          limit={limit}
        />
        <FreemiumModal
          namespace={notification.modal.namespace}
          width={notification.modal.width}
          title={notification.modal.title}
          body={notification.modal.body()}
          btnCaption={notification.modal.btnCaption}
          closeModal={this.toggleShowModalFlagClose}
          showModalFlag={this.state.showModalFlag}
          backgroundColor={FreemiumBlue}
          segmentNotificationType={notification.type.segmentNotificationType}
        />
      </React.Fragment>
    );
  }
}

FreemiumNurtureBar.propTypes = {
  currentAccountUsage: PropTypes.shape({
    users: PropTypes.number.isRequired,
    schedules: PropTypes.number.isRequired,
    escalationPolicies: PropTypes.number.isRequired,
  }).isRequired,
};

const mapStateToProps = state => ({
  currentAccountUsage: {
    users: getUsersCount(state),
    schedules: getSchedulesCount(state),
    escalationPolicies: getEscalationPoliciesCount(state),
  },
});

export default connect(mapStateToProps)(FreemiumNurtureBar);
